import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

export interface TokenResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  refreshExpiresIn: number;
}

@Injectable({
  providedIn: 'root',
})
export class AuthTokenService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);

  private readonly STORAGE_KEY_ACCESS = 'accessToken';
  private readonly STORAGE_KEY_REFRESH = 'refreshToken';
  private readonly STORAGE_KEY_EXPIRES = 'tokenExpiresAt';
  private readonly STORAGE_KEY_REFRESH_EXPIRES = 'refreshExpiresAt';

  private accessToken: string | null = null;
  private refreshToken: string | null = null;

  constructor() {
    if (this.isBrowser) {
      this.loadTokens();
    }
  }

  getAccessToken(): string | null {
    if (!this.accessToken && this.isBrowser) {
      this.accessToken = localStorage.getItem(this.STORAGE_KEY_ACCESS);
    }
    return this.accessToken;
  }

  getRefreshToken(): string | null {
    if (!this.refreshToken && this.isBrowser) {
      this.refreshToken = localStorage.getItem(this.STORAGE_KEY_REFRESH);
    }
    return this.refreshToken;
  }

  saveTokens(response: TokenResponse): void {
    if (!this.isBrowser) return;

    this.accessToken = response.accessToken;
    localStorage.setItem(this.STORAGE_KEY_ACCESS, response.accessToken);

    this.refreshToken = response.refreshToken;
    localStorage.setItem(this.STORAGE_KEY_REFRESH, response.refreshToken);

    const accessExpiresAt = Date.now() + (response.expiresIn * 1000);
    localStorage.setItem(this.STORAGE_KEY_EXPIRES, accessExpiresAt.toString());

    const refreshExpiresAt = Date.now() + (response.refreshExpiresIn * 1000);
    localStorage.setItem(this.STORAGE_KEY_REFRESH_EXPIRES, refreshExpiresAt.toString());
  }

  updateAccessToken(accessToken: string, expiresIn: number): void {
    if (!this.isBrowser) return;

    this.accessToken = accessToken;
    localStorage.setItem(this.STORAGE_KEY_ACCESS, accessToken);

    const expiresAt = Date.now() + (expiresIn * 1000);
    localStorage.setItem(this.STORAGE_KEY_EXPIRES, expiresAt.toString());
  }

  clearTokens(): void {
    if (!this.isBrowser) return;

    this.accessToken = null;
    this.refreshToken = null;

    localStorage.removeItem(this.STORAGE_KEY_ACCESS);
    localStorage.removeItem(this.STORAGE_KEY_REFRESH);
    localStorage.removeItem(this.STORAGE_KEY_EXPIRES);
    localStorage.removeItem(this.STORAGE_KEY_REFRESH_EXPIRES);
  }

  isAccessTokenExpiringSoon(thresholdMinutes: number = 3): boolean {
    if (!this.isBrowser) return false;

    const expiresAt = localStorage.getItem(this.STORAGE_KEY_EXPIRES);
    if (!expiresAt) return true;

    const expirationTime = parseInt(expiresAt, 10);
    const thresholdTime = Date.now() + (thresholdMinutes * 60 * 1000);

    return thresholdTime >= expirationTime;
  }

  isAccessTokenExpired(): boolean {
    if (!this.isBrowser) return true;

    const expiresAt = localStorage.getItem(this.STORAGE_KEY_EXPIRES);
    if (!expiresAt) return true;

    const expirationTime = parseInt(expiresAt, 10);
    return Date.now() >= expirationTime;
  }

  isRefreshTokenExpired(): boolean {
    if (!this.isBrowser) return true;

    const refreshExpiresAt = localStorage.getItem(this.STORAGE_KEY_REFRESH_EXPIRES);
    if (!refreshExpiresAt) return true;

    const expirationTime = parseInt(refreshExpiresAt, 10);
    return Date.now() >= expirationTime;
  }

  getAccessTokenTimeRemaining(): number {
    if (!this.isBrowser) return 0;

    const expiresAt = localStorage.getItem(this.STORAGE_KEY_EXPIRES);
    if (!expiresAt) return 0;

    const expirationTime = parseInt(expiresAt, 10);
    const remaining = Math.max(0, expirationTime - Date.now());
    return Math.floor(remaining / 1000);
  }

  isAuthenticated(): boolean {
    const hasAccessToken = !!this.getAccessToken();
    const hasRefreshToken = !!this.getRefreshToken();

    if (this.isRefreshTokenExpired()) {
      return false;
    }

    return hasAccessToken || hasRefreshToken;
  }

  private loadTokens(): void {
    if (!this.isBrowser) return;

    this.accessToken = localStorage.getItem(this.STORAGE_KEY_ACCESS);
    this.refreshToken = localStorage.getItem(this.STORAGE_KEY_REFRESH);
  }

  saveToken(token: string): void {
    if (!this.isBrowser) return;
    this.accessToken = token;
    localStorage.setItem(this.STORAGE_KEY_ACCESS, token);
  }

  clearToken(): void {
    this.clearTokens();
  }
}
